/**
 * Tile painting.
 *
 * Every tile is drawn with fillRect calls rather than blitted from a sprite sheet, and the
 * scatter of tufts, pebbles and foam comes from a hash of the tile's coordinates. The result is
 * that no two grass tiles are identical but the map is byte-for-byte the same on every reload —
 * which is what stops a generated world from looking generated.
 */

import { palette } from '../art/palette';
import type { Biome } from '../art/palette';
import { biomeFor } from '../art/palette';
import { Terrain, TILE, WATER, tileAt, type World } from './world';

type Ctx = CanvasRenderingContext2D;

function hash(x: number, y: number, salt: number): number {
  let h = (x * 374761393 + y * 668265263 + salt * 2246822519) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

const px = (ctx: Ctx, x: number, y: number, w = 1, h = 1) => ctx.fillRect(x, y, w, h);

/* ------------------------------------------------------------------ terrain */

function paintWater(ctx: Ctx, ox: number, oy: number, x: number, y: number, terrain: Terrain): void {
  const base =
    terrain === Terrain.Deep
      ? palette.seaDeep
      : terrain === Terrain.Shallow
        ? palette.shallow
        : palette.sea;
  ctx.fillStyle = base;
  px(ctx, ox, oy, TILE, TILE);

  // Two darker bands per tile give the sea a horizon-line rhythm without any animation cost.
  ctx.fillStyle =
    terrain === Terrain.Deep
      ? palette.seaDeepShade
      : terrain === Terrain.Shallow
        ? palette.seaLight
        : '#125b95';
  const offset = Math.floor(hash(x, y, 3) * 6);
  px(ctx, ox, oy + 4 + (offset % 3), TILE, 1);
  px(ctx, ox, oy + 11 - (offset % 2), TILE, 1);
}

function paintFog(ctx: Ctx, ox: number, oy: number, x: number, y: number): void {
  ctx.fillStyle = palette.seaDeepShade;
  px(ctx, ox, oy, TILE, TILE);
  ctx.fillStyle = 'rgba(255,255,255,0.05)';
  const n = hash(x, y, 91);
  px(ctx, ox + Math.floor(n * 12), oy + Math.floor(hash(x, y, 92) * 14), 4, 1);
}

function paintGround(ctx: Ctx, ox: number, oy: number, x: number, y: number, biome: Biome): void {
  ctx.fillStyle = biome.ground;
  px(ctx, ox, oy, TILE, TILE);

  for (let i = 0; i < 7; i++) {
    const n = hash(x * 7 + i, y * 13 + i, 11);
    const dx = Math.floor(hash(x + i, y, 17) * TILE);
    const dy = Math.floor(hash(x, y + i, 19) * TILE);
    ctx.fillStyle = n > 0.55 ? biome.groundAlt : biome.groundDark;
    px(ctx, ox + dx, oy + dy, 1, 1);
  }

  // A tuft on roughly one tile in five. Three pixels is enough to read as grass.
  if (hash(x, y, 23) > 0.8) {
    ctx.fillStyle = biome.groundDark;
    const dx = 3 + Math.floor(hash(x, y, 29) * 9);
    const dy = 5 + Math.floor(hash(x, y, 31) * 7);
    px(ctx, ox + dx, oy + dy, 1, 2);
    px(ctx, ox + dx - 1, oy + dy + 1, 1, 1);
    px(ctx, ox + dx + 1, oy + dy + 1, 1, 1);
  }
}

function paintSand(ctx: Ctx, ox: number, oy: number, x: number, y: number): void {
  ctx.fillStyle = palette.sand;
  px(ctx, ox, oy, TILE, TILE);
  ctx.fillStyle = palette.sandDark;
  for (let i = 0; i < 5; i++) {
    px(
      ctx,
      ox + Math.floor(hash(x + i, y * 3, 41) * TILE),
      oy + Math.floor(hash(x * 3, y + i, 43) * TILE),
      1,
      1,
    );
  }
}

function paintPath(
  ctx: Ctx,
  ox: number,
  oy: number,
  x: number,
  y: number,
  world: World,
  biome: Biome,
): void {
  ctx.fillStyle = biome.path;
  px(ctx, ox, oy, TILE, TILE);
  ctx.fillStyle = biome.pathDark;
  for (let i = 0; i < 4; i++) {
    px(
      ctx,
      ox + Math.floor(hash(x + i, y, 53) * TILE),
      oy + Math.floor(hash(x, y + i, 59) * TILE),
      1,
      1,
    );
  }
  // Darken only the sides where the path actually ends, so it reads as a trodden track.
  const isPath = (dx: number, dy: number) => {
    const t = tileAt(world, x + dx, y + dy);
    return t === Terrain.Path || t === Terrain.Dock;
  };
  ctx.fillStyle = biome.pathDark;
  if (!isPath(0, -1)) px(ctx, ox, oy, TILE, 1);
  if (!isPath(0, 1)) px(ctx, ox, oy + TILE - 1, TILE, 1);
  if (!isPath(-1, 0)) px(ctx, ox, oy, 1, TILE);
  if (!isPath(1, 0)) px(ctx, ox + TILE - 1, oy, 1, TILE);
}

/** [x, width] for each consecutive row of a silhouette, top down. */
type Silhouette = ReadonlyArray<readonly [number, number]>;

function paintBlob(
  ctx: Ctx,
  ox: number,
  oy: number,
  top: number,
  rows: Silhouette,
  fill: string,
  outline = palette.black,
): void {
  // Outline first as a slightly fatter copy, then the fill on top — one pass each, and the rows
  // are consecutive, so nothing shows through between them.
  ctx.fillStyle = outline;
  rows.forEach(([sx, w], index) => px(ctx, ox + sx - 1, oy + top + index, w + 2, 1));
  px(ctx, ox + (rows[0]?.[0] ?? 0), oy + top - 1, rows[0]?.[1] ?? 0, 1);
  px(
    ctx,
    ox + (rows[rows.length - 1]?.[0] ?? 0),
    oy + top + rows.length,
    rows[rows.length - 1]?.[1] ?? 0,
    1,
  );
  ctx.fillStyle = fill;
  rows.forEach(([sx, w], index) => px(ctx, ox + sx, oy + top + index, w, 1));
}

const TREE_CANOPY: Silhouette = [
  [5, 6],
  [4, 8],
  [3, 10],
  [2, 12],
  [2, 12],
  [2, 12],
  [3, 10],
  [4, 8],
  [5, 6],
];

const ROCK_BODY: Silhouette = [
  [5, 6],
  [3, 10],
  [2, 12],
  [2, 12],
  [3, 10],
];

function paintTree(ctx: Ctx, ox: number, oy: number, x: number, y: number, biome: Biome): void {
  paintGround(ctx, ox, oy, x, y, biome);
  const [light, mid, trunk] = biome.tree;

  ctx.fillStyle = palette.black;
  px(ctx, ox + 6, oy + 11, 4, 5);
  ctx.fillStyle = trunk;
  px(ctx, ox + 7, oy + 11, 2, 4);

  paintBlob(ctx, ox, oy, 2, TREE_CANOPY, mid);

  // A highlight in the top-left corner is the whole of the lighting model, and it is enough.
  ctx.fillStyle = light;
  px(ctx, ox + 6, oy + 3, 4, 1);
  px(ctx, ox + 5, oy + 4, 3, 1);
  px(ctx, ox + 4, oy + 5, 2, 1);
  ctx.fillStyle = biome.tree[1];
  px(ctx, ox + 4, oy + 9, 8, 1);
}

function paintRock(ctx: Ctx, ox: number, oy: number, x: number, y: number, biome: Biome): void {
  paintGround(ctx, ox, oy, x, y, biome);
  paintBlob(ctx, ox, oy, 7, ROCK_BODY, palette.rock);
  ctx.fillStyle = palette.rockLight;
  px(ctx, ox + 6, oy + 8, 4, 1);
  px(ctx, ox + 4, oy + 9, 3, 1);
  ctx.fillStyle = palette.rockDark;
  px(ctx, ox + 4, oy + 11, 8, 1);
}

function paintCliff(ctx: Ctx, ox: number, oy: number): void {
  ctx.fillStyle = palette.cliff;
  px(ctx, ox, oy, TILE, TILE);
  ctx.fillStyle = palette.cliffDark;
  px(ctx, ox, oy + TILE - 4, TILE, 4);
  ctx.fillStyle = palette.black;
  px(ctx, ox, oy + TILE - 1, TILE, 1);
  ctx.fillStyle = '#a5825c';
  px(ctx, ox, oy, TILE, 2);
}

function paintDock(ctx: Ctx, ox: number, oy: number): void {
  ctx.fillStyle = palette.wood;
  px(ctx, ox, oy, TILE, TILE);
  ctx.fillStyle = palette.woodDark;
  for (let i = 2; i < TILE; i += 4) px(ctx, ox, oy + i, TILE, 1);
  ctx.fillStyle = palette.black;
  px(ctx, ox, oy, 1, TILE);
  px(ctx, ox + TILE - 1, oy, 1, TILE);
}

/* ------------------------------------------------------------------ painted by hand */

/**
 * The terrain below only exists because somebody can now draw an island rather than grow one.
 *
 * Each one is chosen to do a job the generator never needed: a bridge is the only way to cross
 * water on foot, a fence is a wall you can see over, a hut is somewhere for a person to be from,
 * and flowers and tall grass are the two cheapest ways to stop a large field of grass reading as
 * a texture. All of them are painted, not blitted, so they inherit their island's biome.
 */
function paintBridge(ctx: Ctx, ox: number, oy: number, x: number, y: number, world: World): void {
  // The water goes down first: a bridge is a thing over something, and a bridge tile with a grass
  // tile showing at its edges is just a brown rectangle.
  paintWater(ctx, ox, oy, x, y, Terrain.Shallow);

  const spans = (dx: number, dy: number) => tileAt(world, x + dx, y + dy) === Terrain.Bridge;
  // A bridge one tile wide runs whichever way its neighbours do. Both or neither reads as a deck.
  const horizontal = (spans(-1, 0) || spans(1, 0)) && !(spans(0, -1) || spans(0, 1));

  ctx.fillStyle = palette.wood;
  if (horizontal) px(ctx, ox, oy + 2, TILE, TILE - 4);
  else px(ctx, ox + 2, oy, TILE - 4, TILE);

  ctx.fillStyle = palette.woodDark;
  if (horizontal) {
    for (let i = 1; i < TILE; i += 4) px(ctx, ox + i, oy + 2, 1, TILE - 4);
    px(ctx, ox, oy + 2, TILE, 1);
    px(ctx, ox, oy + TILE - 3, TILE, 1);
  } else {
    for (let i = 1; i < TILE; i += 4) px(ctx, ox + 2, oy + i, TILE - 4, 1);
    px(ctx, ox + 2, oy, 1, TILE);
    px(ctx, ox + TILE - 3, oy, 1, TILE);
  }
}

function paintFlowers(ctx: Ctx, ox: number, oy: number, x: number, y: number, biome: Biome): void {
  paintGround(ctx, ox, oy, x, y, biome);
  const colors = [palette.bloom, palette.bloomAlt, palette.bloomPale];
  for (let i = 0; i < 4; i++) {
    const n = hash(x * 5 + i, y * 11 + i, 101);
    if (n < 0.35) continue;
    const dx = 2 + Math.floor(hash(x + i, y, 103) * 12);
    const dy = 2 + Math.floor(hash(x, y + i, 107) * 12);
    // Stem first, then the head on top of it — one pixel each, and it still reads as a flower.
    ctx.fillStyle = biome.groundDark;
    px(ctx, ox + dx, oy + dy + 1, 1, 2);
    ctx.fillStyle = colors[Math.floor(n * colors.length) % colors.length]!;
    px(ctx, ox + dx, oy + dy, 1, 1);
    px(ctx, ox + dx - (n > 0.7 ? 1 : 0), oy + dy, 1, 1);
  }
}

function paintTallGrass(ctx: Ctx, ox: number, oy: number, x: number, y: number, biome: Biome): void {
  paintGround(ctx, ox, oy, x, y, biome);
  ctx.fillStyle = palette.tallGrass;
  for (let i = 0; i < 6; i++) {
    const dx = 1 + Math.floor(hash(x * 3 + i, y, 109) * 14);
    const dy = 4 + Math.floor(hash(x, y * 3 + i, 113) * 8);
    px(ctx, ox + dx, oy + dy, 1, 4);
    px(ctx, ox + dx + 1, oy + dy + 2, 1, 2);
  }
  ctx.fillStyle = palette.tallGrassDark;
  for (let i = 0; i < 3; i++) {
    const dx = 2 + Math.floor(hash(x + i, y * 7, 127) * 12);
    px(ctx, ox + dx, oy + 8, 1, 5);
  }
}

const BUSH_BODY: Silhouette = [
  [5, 6],
  [3, 10],
  [2, 12],
  [2, 12],
  [3, 10],
  [5, 6],
];

function paintBush(ctx: Ctx, ox: number, oy: number, x: number, y: number, biome: Biome): void {
  paintGround(ctx, ox, oy, x, y, biome);
  paintBlob(ctx, ox, oy, 5, BUSH_BODY, palette.bush);
  ctx.fillStyle = palette.bushDark;
  px(ctx, ox + 4, oy + 13, 8, 1);
  ctx.fillStyle = biome.tree[0];
  px(ctx, ox + 5, oy + 6, 3, 1);
  px(ctx, ox + 4, oy + 7, 2, 1);
  // A couple of berries, on the same hash as everything else, so no two bushes are the same one.
  if (hash(x, y, 131) > 0.55) {
    ctx.fillStyle = palette.bloom;
    px(ctx, ox + 9, oy + 8, 1, 1);
    px(ctx, ox + 7, oy + 11, 1, 1);
  }
}

function paintFence(ctx: Ctx, ox: number, oy: number, x: number, y: number, world: World): void {
  const isFence = (dx: number, dy: number) => tileAt(world, x + dx, y + dy) === Terrain.Fence;

  ctx.fillStyle = palette.black;
  px(ctx, ox + 3, oy + 4, 3, 11);
  px(ctx, ox + 10, oy + 4, 3, 11);
  ctx.fillStyle = palette.fence;
  px(ctx, ox + 4, oy + 5, 1, 9);
  px(ctx, ox + 11, oy + 5, 1, 9);

  // Rails only reach towards neighbours that are also fence, so a run reads as one fence and a
  // single post reads as a post rather than as a fence with two broken ends.
  const rail = (top: number) => {
    ctx.fillStyle = palette.black;
    if (isFence(-1, 0)) px(ctx, ox, oy + top - 1, 5, 4);
    if (isFence(1, 0)) px(ctx, ox + 10, oy + top - 1, 6, 4);
    px(ctx, ox + 3, oy + top - 1, 10, 4);
    ctx.fillStyle = palette.fenceDark;
    if (isFence(-1, 0)) px(ctx, ox, oy + top, 5, 2);
    if (isFence(1, 0)) px(ctx, ox + 10, oy + top, 6, 2);
    px(ctx, ox + 3, oy + top, 10, 2);
  };
  rail(7);
  rail(11);
}

function paintHut(ctx: Ctx, ox: number, oy: number): void {
  ctx.fillStyle = palette.black;
  px(ctx, ox + 1, oy + 1, 14, 15);
  // Roof, then wall, then a door — three shapes is a house at this size and four is a mess.
  ctx.fillStyle = palette.roof;
  px(ctx, ox + 2, oy + 2, 12, 5);
  ctx.fillStyle = palette.roofDark;
  px(ctx, ox + 2, oy + 6, 12, 1);
  px(ctx, ox + 2, oy + 2, 12, 1);
  ctx.fillStyle = palette.hutWall;
  px(ctx, ox + 3, oy + 7, 10, 8);
  ctx.fillStyle = palette.hutWallDark;
  px(ctx, ox + 3, oy + 14, 10, 1);
  ctx.fillStyle = palette.black;
  px(ctx, ox + 6, oy + 9, 4, 6);
  ctx.fillStyle = palette.woodDark;
  px(ctx, ox + 7, oy + 10, 2, 5);
}

/* ------------------------------------------------------------------ coastline */

/**
 * Foam where water meets land. Drawn on the water side so it never eats into the beach, and only
 * on the two edges that face the land, which is what gives the coast direction.
 */
function paintFoam(ctx: Ctx, ox: number, oy: number, x: number, y: number, world: World): void {
  const land = (dx: number, dy: number) => {
    const t = tileAt(world, x + dx, y + dy);
    return !WATER.has(t) && t !== Terrain.Fog;
  };

  ctx.fillStyle = palette.foam;
  const dash = Math.floor(hash(x, y, 67) * 4);
  if (land(0, -1)) for (let i = dash % 3; i < TILE; i += 3) px(ctx, ox + i, oy, 2, 2);
  if (land(0, 1)) for (let i = (dash + 1) % 3; i < TILE; i += 3) px(ctx, ox + i, oy + TILE - 2, 2, 2);
  if (land(-1, 0)) for (let i = dash % 3; i < TILE; i += 3) px(ctx, ox, oy + i, 2, 2);
  if (land(1, 0)) for (let i = (dash + 2) % 3; i < TILE; i += 3) px(ctx, ox + TILE - 2, oy + i, 2, 2);
}

/* ------------------------------------------------------------------ the static layer */

/**
 * `into` lets a caller hand back the canvas it got last time.
 *
 * The game renders this layer once and keeps it. The world builder renders it again on every
 * painted tile, and a 64×64 island is a 1024×1024 canvas — four megabytes of fresh allocation per
 * pointer event, handed to the garbage collector while somebody is dragging a brush. Reusing the
 * surface costs one parameter and takes that entirely off the table.
 */
export function renderGroundLayer(
  world: World,
  biomeOf: (x: number, y: number) => Biome,
  into?: HTMLCanvasElement,
): HTMLCanvasElement {
  const canvas = into ?? document.createElement('canvas');
  const width = world.width * TILE;
  const height = world.height * TILE;
  // Assigning width/height also clears the canvas, which is what a repaint wants — but only do it
  // when the size actually changed, or reuse would be no cheaper than allocating.
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  for (let y = 0; y < world.height; y++) {
    for (let x = 0; x < world.width; x++) {
      const ox = x * TILE;
      const oy = y * TILE;
      const terrain = tileAt(world, x, y);
      const biome = biomeOf(x, y);

      switch (terrain) {
        case Terrain.Fog:
          paintFog(ctx, ox, oy, x, y);
          break;
        case Terrain.Deep:
        case Terrain.Sea:
        case Terrain.Shallow:
          paintWater(ctx, ox, oy, x, y, terrain);
          break;
        case Terrain.Sand:
          paintSand(ctx, ox, oy, x, y);
          break;
        case Terrain.Path:
          paintPath(ctx, ox, oy, x, y, world, biome);
          break;
        case Terrain.Tree:
          paintTree(ctx, ox, oy, x, y, biome);
          break;
        case Terrain.Rock:
          paintRock(ctx, ox, oy, x, y, biome);
          break;
        case Terrain.Cliff:
          paintCliff(ctx, ox, oy);
          break;
        case Terrain.Dock:
          paintDock(ctx, ox, oy);
          break;
        case Terrain.Bridge:
          paintBridge(ctx, ox, oy, x, y, world);
          break;
        case Terrain.Flowers:
          paintFlowers(ctx, ox, oy, x, y, biome);
          break;
        case Terrain.TallGrass:
          paintTallGrass(ctx, ox, oy, x, y, biome);
          break;
        case Terrain.Bush:
          paintBush(ctx, ox, oy, x, y, biome);
          break;
        case Terrain.Fence:
          paintGround(ctx, ox, oy, x, y, biome);
          paintFence(ctx, ox, oy, x, y, world);
          break;
        case Terrain.Hut:
          paintGround(ctx, ox, oy, x, y, biome);
          paintHut(ctx, ox, oy);
          break;
        default:
          paintGround(ctx, ox, oy, x, y, biome);
      }
    }
  }

  // Second pass so foam can look at finished neighbours.
  for (let y = 0; y < world.height; y++) {
    for (let x = 0; x < world.width; x++) {
      const terrain = tileAt(world, x, y);
      if (terrain === Terrain.Shallow) paintFoam(ctx, x * TILE, y * TILE, x, y, world);
    }
  }

  return canvas;
}

/**
 * The only thing that moves in the background. Four frames of sparkle on open water — cheap
 * enough to run on a five-year-old phone, and it is the difference between a map and a sea.
 */
export function drawWaterSparkle(
  ctx: Ctx,
  world: World,
  frame: number,
  view: { x0: number; y0: number; x1: number; y1: number },
): void {
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  for (let y = view.y0; y <= view.y1; y++) {
    for (let x = view.x0; x <= view.x1; x++) {
      const terrain = tileAt(world, x, y);
      if (!WATER.has(terrain)) continue;
      const phase = Math.floor(hash(x, y, 71) * 4);
      if ((phase + frame) % 4 !== 0) continue;
      const dx = Math.floor(hash(x, y, 73) * 12) + 2;
      const dy = Math.floor(hash(x, y, 79) * 12) + 2;
      ctx.fillRect(x * TILE + dx, y * TILE + dy, 3, 1);
      ctx.fillRect(x * TILE + dx + 1, y * TILE + dy + 1, 1, 1);
    }
  }
}

export { biomeFor };
